import { type AppError } from '@/api/errors';
import {
  type DepositMode,
  isKnownStatus,
  isTerminalStatus,
  type PaymentMethodCode,
} from '@/constants/deposit';
import { logger } from '@/lib/logger';
import { type WalletInstructions } from '@/schemas/cashierSession.schema';
import { type CashierMethodTile } from '@/schemas/methods.schema';

export type ClientIntent =
  | { step: 'none' }
  | {
      step: 'payerWallet';
      gatewayAccountId: string;
      label: string;
      paymentMethodCode: PaymentMethodCode;
    }
  | { step: 'proof' };

export interface ViewStateInput {
  phase: 'idle' | 'resolving' | 'resolved';
  status: string | null;
  mode: DepositMode | null;
  methods: CashierMethodTile[] | null;
  walletInstructions: WalletInstructions | null;
  expiresAt: string | null;
  error: AppError | null;
  clientIntent: ClientIntent;
}

export type ViewState =
  | 'resolving'
  | 'invalidLink'
  | 'unavailable'
  | 'selectMethod'
  | 'collectPayerWallet'
  | 'walletInstructions'
  | 'submitProof'
  | 'processing'
  | 'underReview'
  | 'succeeded'
  | 'failed'
  | 'expired'
  | 'error';

function terminalView(status: string): ViewState {
  if (status === 'completed') return 'succeeded';
  if (status === 'expired') return 'expired';
  return 'failed';
}

/**
 * Total, pure, never throws. Precedence is the documented order in State-Machine.md §3.
 *
 * Rule 2 is `phase === 'resolving' || (phase === 'idle' && error === null)` rather than
 * `phase !== 'resolved'`. The latter would trap a failed resolve on the branded skeleton forever.
 */
export function deriveViewState(input: ViewStateInput): ViewState {
  const { phase, status, mode, methods, error, clientIntent } = input;

  if (error?.status === 401) return 'invalidLink';

  if (phase === 'resolving' || (phase === 'idle' && error === null)) return 'resolving';

  if (status !== null && isKnownStatus(status) && isTerminalStatus(status)) {
    return terminalView(status);
  }

  if (status !== null && !isKnownStatus(status)) {
    logger.debug('[state] unknown deposit status', { status });
    return 'error';
  }

  // A proof failure must not unmount the form: doing so drops `input.files` and forces the player
  // to photograph the receipt again. The form renders localized field/toast feedback in place.
  if (mode === 'wallet' && status === 'awaiting_payment' && clientIntent.step === 'proof') {
    return 'submitProof';
  }

  // A 422 means the selected route is no longer eligible. Keep the player on the refreshed
  // catalogue so they can choose another backend-offered option; never fall back automatically.
  if (status === 'initiated' && mode === null && error?.status === 422) {
    return methods !== null && methods.length === 0 ? 'unavailable' : 'selectMethod';
  }

  // 400 + fieldErrors stay on the form (CSH-166). Other errors still take the error screen.
  if (error !== null && (error.status !== 400 || error.fieldErrors === undefined)) {
    return 'error';
  }

  if (status === 'processing') return 'processing';
  if (status === 'pending_review') return 'underReview';

  if (mode === 'wallet' && status === 'awaiting_payment') {
    return 'walletInstructions';
  }

  if (methods !== null && methods.length === 0) return 'unavailable';

  if (status === 'initiated' && mode === null) {
    return clientIntent.step === 'payerWallet' ? 'collectPayerWallet' : 'selectMethod';
  }

  logger.debug('[state] derivation fallback', { phase, status, mode });
  return 'error';
}
